import { Button } from '@/components/ui/Button'
import { Box, Grid, HStack, Text, VStack } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'

/**
 * Arendator Detail Card Component
 * Displays arendator information in a card format with edit/remove actions
 */
export function ArendatorCard({ arendator, onEdit, onRemove }) {
  const { t } = useTranslation()

  if (!arendator) return null

  return (
    <Box
      p={ 4 }
      border="1px solid"
      borderColor="#DFE1E6"
      borderRadius="sm"
      bg="white"
    >
      <VStack spacing={ 3 } align="stretch">
        {/* Name and Actions */}
        <HStack justify="space-between">
          <Text fontSize="md" fontWeight="600" color="#172B4D">
            {arendator.client_name || arendator.name || t('arendators.unnamed')}
          </Text>
          <HStack spacing={ 2 }>
            <Button
              size="sm"
              variant="outline"
              onClick={onEdit}
            >
              {t('common.edit')}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              color="#DE350B"
              onClick={onRemove}
              _hover={{ bg: '#FFEBE6' }}
            >
              {t('common.remove')}
            </Button>
          </HStack>
        </HStack>

        {/* Contact Information */}
        <Grid templateColumns="repeat(2, 1fr)" gap={ 3 }>
          {(arendator.client_phone || arendator.phone_first) && (
            <Box>
              <Text fontSize="xs" color="#5E6C84" mb={ 1 }>
                {t('arendators.phoneFirst')}
              </Text>
              <Text fontSize="sm" color="#172B4D">
                {arendator.client_phone || arendator.phone_first}
              </Text>
            </Box>
          )}
          {arendator.phone_second && (
            <Box>
              <Text fontSize="xs" color="#5E6C84" mb={ 1 }>
                {t('arendators.phoneSecond')}
              </Text>
              <Text fontSize="sm" color="#172B4D">
                {arendator.phone_second}
              </Text>
            </Box>
          )}
          {arendator.email && (
            <Box>
              <Text fontSize="xs" color="#5E6C84" mb={ 1 }>
                {t('arendators.email')}
              </Text>
              <Text fontSize="sm" color="#172B4D">
                {arendator.email}
              </Text>
            </Box>
          )}
          {arendator.commission && (
            <Box>
              <Text fontSize="xs" color="#5E6C84" mb={ 1 }>
                {t('arendators.commission')}
              </Text>
              <Text fontSize="sm" color="#172B4D">
                {arendator.commission}
              </Text>
            </Box>
          )}
        </Grid>

        {/* Price Range */}
        {(arendator.price_from || arendator.price_to) && (
          <Box>
            <Text fontSize="xs" color="#5E6C84" mb={ 1 }>
              {t('arendators.priceRange')}
            </Text>
            <Text fontSize="sm" color="#172B4D">
              {arendator.price_from ? `$${arendator.price_from.toLocaleString()}` : '—'}
              {' - '}
              {arendator.price_to ? `$${arendator.price_to.toLocaleString()}` : '—'}
            </Text>
          </Box>
        )}

        {/* Rooms Range */}
        {(arendator.rooms_from || arendator.rooms_to) && (
          <Box>
            <Text fontSize="xs" color="#5E6C84" mb={ 1 }>
              {t('arendators.roomsRange')}
            </Text>
            <Text fontSize="sm" color="#172B4D">
              {arendator.rooms_from || '—'} - {arendator.rooms_to || '—'}
            </Text>
          </Box>
        )}

        {/* Floor Range */}
        {(arendator.floor || arendator.floor_to) && (
          <Box>
            <Text fontSize="xs" color="#5E6C84" mb={ 1 }>
              {t('arendators.floorRange')}
            </Text>
            <Text fontSize="sm" color="#172B4D">
              {arendator.floor || '—'} - {arendator.floor_to || '—'}
            </Text>
          </Box>
        )}

        {/* Area Range */}
        {(arendator.area_from || arendator.area_to) && (
          <Box>
            <Text fontSize="xs" color="#5E6C84" mb={ 1 }>
              {t('arendators.areaRange')}
            </Text>
            <Text fontSize="sm" color="#172B4D">
              {arendator.area_from ? `${arendator.area_from} м²` : '—'}
              {' - '}
              {arendator.area_to ? `${arendator.area_to} м²` : '—'}
            </Text>
          </Box>
        )}

        {/* Comment */}
        {arendator.comment && (
          <Box>
            <Text fontSize="xs" color="#5E6C84" mb={ 1 }>
              {t('arendators.comment')}
            </Text>
            <Text fontSize="sm" color="#172B4D">
              {arendator.comment}
            </Text>
          </Box>
        )}
      </VStack>
    </Box>
  )
}

export default ArendatorCard
